/**
 * SRD coverage (P0-4b deliverable 5): one row for every field of every
 * Open5e census model that normalizes into StatBlockV1, SpellV1 or ItemV1.
 * A row is one of:
 *
 * - `{model, field, target}`: the schema path the field fills;
 * - `{model, field, target: null, reason}`: the field is dropped; every such
 *   row is listed in `srd-null-allow.ts`, which the Fable reviewer approves;
 * - `{model, field, derivedFromText: true, into}`: the value is taken from
 *   the `desc` text by P0-11b's parser ("text wins"); the structured field is
 *   only compared. `into` is the schema path the parsed value lands in.
 *
 * Paths use dot notation with `[]` for arrays. Each row's model normalizes
 * into the schema `SRD_MODEL_SCHEMAS` names. The field names come from
 * `packages/srd/census/census.json`.
 */
export const SRD_MODEL_SCHEMAS = {
    'api_v2.creature': 'StatBlockV1',
    'api_v2.creatureaction': 'StatBlockV1',
    'api_v2.creatureactionattack': 'StatBlockV1',
    'api_v2.creaturetrait': 'StatBlockV1',
    'api_v2.spell': 'SpellV1',
    'api_v2.spellcastingoption': 'SpellV1',
    'api_v2.magicitem': 'ItemV1',
    'api_v2.item': 'ItemV1',
    'api_v2.weapon': 'ItemV1',
    'api_v2.armor': 'ItemV1',
    'api_v2.weaponproperty': 'ItemV1',
    'api_v2.weaponpropertyassignment': 'ItemV1',
};
export const SRD_MODELS = Object.keys(SRD_MODEL_SCHEMAS);
const C = 'api_v2.creature';
const ABILITY_FIELDS = [
    ['strength', 'str'],
    ['dexterity', 'dex'],
    ['constitution', 'con'],
    ['intelligence', 'int'],
    ['wisdom', 'wis'],
    ['charisma', 'cha'],
];
const SKILL_FIELDS = [
    'acrobatics',
    'animal_handling',
    'arcana',
    'athletics',
    'deception',
    'history',
    'insight',
    'intimidation',
    'investigation',
    'medicine',
    'nature',
    'perception',
    'performance',
    'persuasion',
    'religion',
    'sleight_of_hand',
    'stealth',
    'survival',
];
const creature = [
    ...ABILITY_FIELDS.map(([name, key]) => ({
        model: C,
        field: `ability_score_${name}`,
        target: `abilities.${key}`,
    })),
    { model: C, field: 'alignment', target: 'alignment' },
    { model: C, field: 'armor_class', target: 'armorClass[].value' },
    { model: C, field: 'armor_detail', target: 'armorClass[].source' },
    { model: C, field: 'blindsight_range', target: 'senses.blindsight' },
    { model: C, field: 'burrow', target: 'speed.burrow' },
    {
        model: C,
        field: 'category',
        target: null,
        reason: 'Upstream bookkeeping: the SRD chapter the block is printed in (Monsters or Miscellaneous Creatures)',
    },
    { model: C, field: 'challenge_rating', target: 'challenge.rating' },
    { model: C, field: 'climb', target: 'speed.climb' },
    { model: C, field: 'condition_immunities', target: 'conditionImmunities[]' },
    { model: C, field: 'condition_immunities_display', target: 'conditionImmunities' },
    { model: C, field: 'damage_immunities', target: 'damageImmunities[].types' },
    { model: C, field: 'damage_immunities_display', target: 'damageImmunities[].qualifier' },
    { model: C, field: 'damage_resistances', target: 'damageResistances[].types' },
    { model: C, field: 'damage_resistances_display', target: 'damageResistances[].qualifier' },
    { model: C, field: 'damage_vulnerabilities', target: 'damageVulnerabilities[].types' },
    {
        model: C,
        field: 'damage_vulnerabilities_display',
        target: 'damageVulnerabilities[].qualifier',
    },
    { model: C, field: 'darkvision_range', target: 'senses.darkvision' },
    { model: C, field: 'document', target: null, reason: 'Foreign key to the Document record' },
    {
        model: C,
        field: 'environments',
        target: null,
        reason: 'Link to Environment, an Open5e addition',
    },
    { model: C, field: 'experience_points_integer', target: 'challenge.xp' },
    { model: C, field: 'fly', target: 'speed.fly' },
    { model: C, field: 'hit_dice', target: 'hitPoints.formula' },
    { model: C, field: 'hit_points', target: 'hitPoints.average' },
    { model: C, field: 'hover', target: 'speed.hover' },
    {
        model: C,
        field: 'illustration',
        target: null,
        reason: 'Image field; Open5e artwork is not used',
    },
    { model: C, field: 'initiative_bonus', target: 'initiativeBonus' },
    { model: C, field: 'languages', target: 'languages.values[]' },
    { model: C, field: 'languages_desc', target: 'languages' },
    { model: C, field: 'name', target: 'name' },
    { model: C, field: 'nonmagical_attack_immunity', target: 'damageImmunities[].bypasses' },
    { model: C, field: 'nonmagical_attack_resistance', target: 'damageResistances[].bypasses' },
    {
        model: C,
        field: 'normal_sight_range',
        target: null,
        reason: "Open5e's default sight range of two miles; a 2014 stat block never prints it",
    },
    { model: C, field: 'passive_perception', target: 'senses.passivePerception' },
    { model: C, field: 'proficiency_bonus', target: 'proficiencyBonus' },
    ...ABILITY_FIELDS.map(([name]) => ({
        model: C,
        field: `saving_throw_${name}`,
        target: 'savingThrows[].bonus',
    })),
    { model: C, field: 'size', target: 'size' },
    ...SKILL_FIELDS.map((name) => ({
        model: C,
        field: `skill_bonus_${name}`,
        target: 'skills[].bonus',
    })),
    {
        model: C,
        field: 'subcategory',
        target: null,
        reason: 'Upstream bookkeeping: the SRD heading the block is printed under ("Hags", "Dragons, Chromatic")',
    },
    { model: C, field: 'swim', target: 'speed.swim' },
    { model: C, field: 'telepathy_range', target: 'languages.telepathy' },
    { model: C, field: 'tremorsense_range', target: 'senses.tremorsense' },
    { model: C, field: 'truesight_range', target: 'senses.truesight' },
    { model: C, field: 'type', target: 'creatureType.value' },
    {
        model: C,
        field: 'unit',
        target: null,
        reason: 'Distance unit column that is null on every record; 2014 stat blocks give distances in feet',
    },
    { model: C, field: 'walk', target: 'speed.walk' },
    {
        model: C,
        field: 'weight',
        target: null,
        reason: "Open5e's object-model weight column (zero on all but one record); a 2014 stat block has no weight",
    },
];
const CA = 'api_v2.creatureaction';
const creatureAction = [
    { model: CA, field: 'action_type', target: 'actions' },
    { model: CA, field: 'desc', target: 'actions[].text' },
    { model: CA, field: 'legendary_action_cost', target: 'legendary.options[].cost' },
    { model: CA, field: 'limited_to_form', target: 'actions[].name' },
    { model: CA, field: 'name', target: 'actions[].name' },
    {
        model: CA,
        field: 'order_in_statblock',
        target: null,
        reason: 'Upstream ordering index; the arrays keep printed order',
    },
    { model: CA, field: 'parent', target: 'actions' },
    { model: CA, field: 'uses_param', target: 'actions[].usage' },
    { model: CA, field: 'uses_type', target: 'actions[].usage' },
];
const CAA = 'api_v2.creatureactionattack';
/** The structured attack rows: never copied into AttackV1, only compared with the parsed text. */
const ATTACK_FIELDS = [
    ['attack_type', 'actions[].attack.kind'],
    ['damage_bonus', 'actions[].attack.damage[].dice.bonus'],
    ['damage_die_count', 'actions[].attack.damage[].dice.count'],
    ['damage_die_type', 'actions[].attack.damage[].dice.die'],
    ['damage_type', 'actions[].attack.damage[].type'],
    ['distance_unit', 'actions[].attack.rangeNormal'],
    ['extra_damage_bonus', 'actions[].attack.riders[].dice.bonus'],
    ['extra_damage_die_count', 'actions[].attack.riders[].dice.count'],
    ['extra_damage_die_type', 'actions[].attack.riders[].dice.die'],
    ['extra_damage_type', 'actions[].attack.riders[].type'],
    ['long_range', 'actions[].attack.rangeLong'],
    ['name', 'actions[].name'],
    ['parent', 'actions[].attack'],
    ['range', 'actions[].attack.rangeNormal'],
    ['reach', 'actions[].attack.reach'],
    ['target_creature_only', 'actions[].attack.target'],
    ['to_hit_mod', 'actions[].attack.toHit'],
];
const creatureActionAttack = ATTACK_FIELDS.map(([field, into]) => ({
    model: CAA,
    field,
    derivedFromText: true,
    into,
}));
const CT = 'api_v2.creaturetrait';
const creatureTrait = [
    { model: CT, field: 'desc', target: 'traits[].text' },
    { model: CT, field: 'name', target: 'traits[].name' },
    { model: CT, field: 'parent', target: 'traits' },
    {
        model: CT,
        field: 'type',
        target: null,
        reason: 'Trait type column that is null on every record',
    },
];
const S = 'api_v2.spell';
const spell = [
    { model: S, field: 'attack_roll', target: 'attackRoll' },
    { model: S, field: 'casting_time', target: 'castingTime' },
    { model: S, field: 'classes', target: 'classes[]' },
    { model: S, field: 'concentration', target: 'duration.concentration' },
    { model: S, field: 'damage_roll', target: 'damage.roll' },
    { model: S, field: 'damage_types', target: 'damage.types[]' },
    { model: S, field: 'desc', target: 'text' },
    { model: S, field: 'document', target: null, reason: 'Foreign key to the Document record' },
    { model: S, field: 'duration', target: 'duration' },
    { model: S, field: 'higher_level', target: 'higherLevels' },
    { model: S, field: 'level', target: 'level' },
    { model: S, field: 'material', target: 'components.material' },
    { model: S, field: 'material_consumed', target: 'components.materialConsumed' },
    { model: S, field: 'material_cost', target: 'components.materialCost' },
    { model: S, field: 'material_specified', target: 'components.materialText' },
    { model: S, field: 'name', target: 'name' },
    { model: S, field: 'range', target: 'range.distance' },
    { model: S, field: 'range_text', target: 'range.kind' },
    { model: S, field: 'range_unit', target: 'range.unit' },
    { model: S, field: 'reaction_condition', target: 'castingTime.reactionTrigger' },
    { model: S, field: 'ritual', target: 'ritual' },
    { model: S, field: 'saving_throw_ability', target: 'saveAbility' },
    { model: S, field: 'school', target: 'school' },
    { model: S, field: 'shape_size', target: 'range.shape.size' },
    { model: S, field: 'shape_size_unit', target: 'range.shape.unit' },
    { model: S, field: 'shape_type', target: 'range.shape.type' },
    { model: S, field: 'somatic', target: 'components.somatic' },
    { model: S, field: 'target_count', target: 'target.count' },
    { model: S, field: 'target_type', target: 'target.type' },
    { model: S, field: 'verbal', target: 'components.verbal' },
];
const SCO = 'api_v2.spellcastingoption';
const spellCastingOption = [
    { model: SCO, field: 'concentration', target: 'upcast[].concentration' },
    { model: SCO, field: 'damage_roll', target: 'upcast[].damageRoll' },
    { model: SCO, field: 'desc', target: 'upcast[].text' },
    { model: SCO, field: 'duration', target: 'upcast[].duration' },
    { model: SCO, field: 'parent', target: 'upcast' },
    { model: SCO, field: 'range', target: 'upcast[].range' },
    { model: SCO, field: 'shape_size', target: 'upcast[].shapeSize' },
    { model: SCO, field: 'target_count', target: 'upcast[].targetCount' },
    { model: SCO, field: 'type', target: 'upcast[].kind' },
];
/** Fields that Item and MagicItem share: both are Open5e objects. */
function itemRows(model) {
    return [
        { model, field: 'armor', target: 'armor' },
        { model, field: 'armor_class', target: 'object.armorClass.value' },
        { model, field: 'armor_detail', target: 'object.armorClass.source' },
        { model, field: 'category', target: 'category' },
        { model, field: 'cost', target: 'cost' },
        { model, field: 'damage_immunities', target: 'object.damageImmunities[].types' },
        { model, field: 'damage_resistances', target: 'object.damageResistances[].types' },
        { model, field: 'damage_vulnerabilities', target: 'object.damageVulnerabilities[].types' },
        { model, field: 'desc', target: 'text' },
        { model, field: 'document', target: null, reason: 'Foreign key to the Document record' },
        { model, field: 'hit_dice', target: 'object.hitDice' },
        { model, field: 'hit_points', target: 'object.hitPoints' },
        { model, field: 'name', target: 'name' },
        { model, field: 'nonmagical_attack_immunity', target: 'object.damageImmunities[].bypasses' },
        { model, field: 'nonmagical_attack_resistance', target: 'object.damageResistances[].bypasses' },
        { model, field: 'size', target: 'object.size' },
        { model, field: 'weapon', target: 'weapon' },
        { model, field: 'weight', target: 'weight' },
    ];
}
const MI = 'api_v2.magicitem';
const magicItem = [
    ...itemRows(MI),
    { model: MI, field: 'attunement_detail', target: 'attunement.requirement' },
    { model: MI, field: 'rarity', target: 'rarity' },
    { model: MI, field: 'requires_attunement', target: 'attunement.required' },
];
const item = itemRows('api_v2.item');
const W = 'api_v2.weapon';
const weapon = [
    { model: W, field: 'damage_dice', target: 'weapon.damage.dice' },
    { model: W, field: 'damage_type', target: 'weapon.damage.type' },
    {
        model: W,
        field: 'distance_unit',
        target: null,
        reason: 'Distance unit column that is null on every record; 2014 weapon ranges are in feet',
    },
    { model: W, field: 'document', target: null, reason: 'Foreign key to the Document record' },
    { model: W, field: 'is_improvised', target: 'weapon.category' },
    { model: W, field: 'is_simple', target: 'weapon.martial' },
    { model: W, field: 'long_range', target: 'weapon.range.long' },
    { model: W, field: 'name', target: 'name' },
    { model: W, field: 'range', target: 'weapon.range.normal' },
];
const A = 'api_v2.armor';
const armor = [
    { model: A, field: 'ac_add_dexmod', target: 'armor.addDex' },
    { model: A, field: 'ac_base', target: 'armor.baseAc' },
    { model: A, field: 'ac_cap_dexmod', target: 'armor.dexCap' },
    { model: A, field: 'document', target: null, reason: 'Foreign key to the Document record' },
    { model: A, field: 'grants_stealth_disadvantage', target: 'armor.stealthDisadvantage' },
    { model: A, field: 'name', target: 'name' },
    { model: A, field: 'strength_score_required', target: 'armor.strength' },
];
const WP = 'api_v2.weaponproperty';
const weaponProperty = [
    {
        model: WP,
        field: 'desc',
        target: null,
        reason: "The property's rules glossary text, the same for every weapon; the card shows the property's label",
    },
    { model: WP, field: 'document', target: null, reason: 'Foreign key to the Document record' },
    { model: WP, field: 'name', target: 'weapon.properties[]' },
    {
        model: WP,
        field: 'type',
        target: null,
        reason: 'Property type column that is null on every record',
    },
];
const WPA = 'api_v2.weaponpropertyassignment';
const weaponPropertyAssignment = [
    { model: WPA, field: 'detail', target: 'weapon.versatile' },
    { model: WPA, field: 'document', target: null, reason: 'Foreign key to the Document record' },
    { model: WPA, field: 'property', target: 'weapon.properties[]' },
    { model: WPA, field: 'weapon', target: 'weapon' },
];
export const SRD_COVERAGE = [
    ...creature,
    ...creatureAction,
    ...creatureActionAttack,
    ...creatureTrait,
    ...spell,
    ...spellCastingOption,
    ...magicItem,
    ...item,
    ...weapon,
    ...armor,
    ...weaponProperty,
    ...weaponPropertyAssignment,
];
//# sourceMappingURL=srd.js.map